import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Grid2 as Grid,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import {
  Chat,
  Search,
  Add,
  Assignment,
  Build,
  AttachFile,
  MoreVert,
  Notifications,
  Archive,
  Delete,
  MarkEmailRead,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const PpMessagingCenterPage = () => {
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const [conversations, setConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState("");
  const [conversationType, setConversationType] = useState("general");
  const [relatedSubmission, setRelatedSubmission] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Mock conversation data
  const mockConversations = useMemo(
    () => [
      {
        id: "CONV001",
        type: "submission",
        subject: "Plan Review Questions - PRS001",
        participants: [
          {
            id: "PP001",
            name: "John Smith, PE",
            role: "private_provider",
            avatar: "JS",
          },
          {
            id: "C001",
            name: "ABC Construction",
            role: "contractor",
            avatar: "AB",
          },
        ],
        lastMessage: {
          id: "MSG001",
          text: "I have uploaded the revised structural plans addressing the foundation details.",
          sender: "ABC Construction",
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          attachments: ["Structural_Plans_Rev01.pdf"],
        },
        unreadCount: 2,
        relatedSubmission: "PRS001",
        status: "active",
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: "CONV002",
        type: "inspection",
        subject: "Framing Inspection Coordination - PKG2024001",
        participants: [
          {
            id: "PP001",
            name: "Mike Johnson",
            role: "inspector",
            avatar: "MJ",
          },
          {
            id: "C002",
            name: "Best Builders LLC",
            role: "contractor",
            avatar: "BB",
          },
        ],
        lastMessage: {
          id: "MSG002",
          text: "The framing inspection is scheduled for tomorrow at 10 AM. Please have all areas accessible.",
          sender: "Mike Johnson",
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          attachments: [],
        },
        unreadCount: 0,
        relatedInspection: "INS002",
        status: "active",
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: "CONV003",
        type: "general",
        subject: "General Questions about Process",
        participants: [
          {
            id: "PP001",
            name: "Jane Doe, PE",
            role: "private_provider",
            avatar: "JD",
          },
          {
            id: "C003",
            name: "Prime Contracting",
            role: "contractor",
            avatar: "PC",
          },
        ],
        lastMessage: {
          id: "MSG003",
          text: "Thank you for the clarification on the stamping process.",
          sender: "Prime Contracting",
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          attachments: [],
        },
        unreadCount: 0,
        status: "active",
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    []
  );

  // Mock data for new conversation
  const contractors = [
    { id: "C001", name: "ABC Construction", license: "CBC012345" },
    { id: "C002", name: "Best Builders LLC", license: "CBC054321" },
    { id: "C003", name: "Prime Contracting", license: "CBC098765" },
  ];

  const submissions = [
    { id: "PRS001", permitNumber: "PKG2024001", address: "123 Oak St" },
    { id: "PRS002", permitNumber: "PKG2024002", address: "456 Pine Ave" },
  ];

  useEffect(() => {
    setConversations(mockConversations);
  }, [mockConversations]);

  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch =
      conv.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.participants.some((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesFilter = filterType === "all" || conv.type === filterType;

    return matchesSearch && matchesFilter;
  });

  const totalUnread = conversations.reduce(
    (sum, conv) => sum + conv.unreadCount,
    0
  );

  const getConversationIcon = (type) => {
    const iconMap = {
      submission: <Assignment color="primary" />,
      inspection: <Build color="secondary" />,
      general: <Chat color="info" />,
    };
    return iconMap[type] || <Chat />;
  };

  const getConversationTypeColor = (type) => {
    const colorMap = {
      submission: "primary",
      inspection: "secondary",
      general: "info",
    };
    return colorMap[type] || "default";
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return time.toLocaleDateString();
  };

  const handleCreateConversation = async () => {
    if (!selectedContractor || !conversationType) return;

    const newConversation = {
      id: `CONV${Date.now()}`,
      type: conversationType,
      subject: `New ${conversationType} conversation`,
      participants: [
        {
          id: "PP001",
          name:
            currentUser?.user?.firstName + " " + currentUser?.user?.lastName,
          role: "private_provider",
          avatar:
            currentUser?.user?.firstName?.[0] +
            currentUser?.user?.lastName?.[0],
        },
        contractors.find((c) => c.id === selectedContractor),
      ],
      lastMessage: null,
      unreadCount: 0,
      relatedSubmission: relatedSubmission || undefined,
      status: "active",
      createdAt: new Date().toISOString(),
    };

    setConversations((prev) => [newConversation, ...prev]);
    setNewConversationOpen(false);
    setSelectedContractor("");
    setConversationType("general");
    setRelatedSubmission("");

    // Navigate to the new conversation
    navigate(`/pp/messaging/${newConversation.id}`);
  };

  const handleMenuOpen = (event, conversation) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleMarkAsRead = () => {
    if (selectedConversation) {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === selectedConversation.id
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    }
    handleMenuClose();
  };

  const handleArchiveConversation = () => {
    if (selectedConversation) {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === selectedConversation.id
            ? { ...conv, status: "archived" }
            : conv
        )
      );
    }
    handleMenuClose();
  };

  const handleDeleteConversation = () => {
    if (selectedConversation) {
      setConversations((prev) =>
        prev.filter((conv) => conv.id !== selectedConversation.id)
      );
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography color="primary" variant="h4">
            Messaging Center
          </Typography>
          {totalUnread > 0 && (
            <Badge badgeContent={totalUnread} color="error" max={99}>
              <Notifications color="primary" />
            </Badge>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewConversationOpen(true)}
        >
          New Conversation
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Filters */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      label="Type"
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="submission">Plan Review</MenuItem>
                      <MenuItem value="inspection">Inspections</MenuItem>
                      <MenuItem value="general">General</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Conversations List */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conversations ({filteredConversations.length})
              </Typography>

              {filteredConversations.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Chat sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No conversations found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start a new conversation to communicate with contractors
                  </Typography>
                </Box>
              ) : (
                <List>
                  {filteredConversations.map((conversation, index) => (
                    <React.Fragment key={conversation.id}>
                      <ListItemButton
                        onClick={() =>
                          navigate(`/pp/messaging/${conversation.id}`)
                        }
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor:
                            conversation.unreadCount > 0
                              ? "action.hover"
                              : "transparent",
                          "&:hover": { bgcolor: "action.selected" },
                        }}
                      >
                        <ListItemIcon>
                          <Badge
                            badgeContent={conversation.unreadCount}
                            color="error"
                          >
                            <Avatar
                              sx={{
                                bgcolor: `${getConversationTypeColor(conversation.type)}.main`,
                              }}
                            >
                              {getConversationIcon(conversation.type)}
                            </Avatar>
                          </Badge>
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight={
                                  conversation.unreadCount > 0
                                    ? "bold"
                                    : "normal"
                                }
                              >
                                {conversation.subject}
                              </Typography>
                              <Chip
                                label={conversation.type}
                                color={getConversationTypeColor(
                                  conversation.type
                                )}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                Participants:{" "}
                                {conversation.participants
                                  .map((p) => p.name)
                                  .join(", ")}
                              </Typography>
                              {conversation.lastMessage && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={
                                      conversation.unreadCount > 0
                                        ? "bold"
                                        : "normal"
                                    }
                                  >
                                    {conversation.lastMessage.sender}:{" "}
                                    {conversation.lastMessage.text.length > 50
                                      ? `${conversation.lastMessage.text.substring(0, 50)}...`
                                      : conversation.lastMessage.text}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {formatTimeAgo(
                                      conversation.lastMessage.timestamp
                                    )}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            ml: "auto",
                          }}
                        >
                          {conversation.lastMessage?.attachments?.length >
                            0 && (
                            <Chip
                              icon={<AttachFile />}
                              label={
                                conversation.lastMessage.attachments.length
                              }
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, conversation)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </ListItemButton>
                      {index < filteredConversations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* New Conversation Dialog */}
      <Dialog
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start New Conversation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Contractor</InputLabel>
                <Select
                  value={selectedContractor}
                  label="Contractor"
                  onChange={(e) => setSelectedContractor(e.target.value)}
                >
                  {contractors.map((contractor) => (
                    <MenuItem key={contractor.id} value={contractor.id}>
                      {contractor.name} ({contractor.license})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Conversation Type</InputLabel>
                <Select
                  value={conversationType}
                  label="Conversation Type"
                  onChange={(e) => setConversationType(e.target.value)}
                >
                  <MenuItem value="general">General Discussion</MenuItem>
                  <MenuItem value="submission">Plan Review Related</MenuItem>
                  <MenuItem value="inspection">Inspection Related</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {(conversationType === "submission" ||
              conversationType === "inspection") && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Related Submission</InputLabel>
                  <Select
                    value={relatedSubmission}
                    label="Related Submission"
                    onChange={(e) => setRelatedSubmission(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {submissions.map((submission) => (
                      <MenuItem key={submission.id} value={submission.id}>
                        {submission.permitNumber} - {submission.address}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewConversationOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateConversation}
            disabled={!selectedContractor || !conversationType}
          >
            Start Conversation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedConversation?.unreadCount > 0 && (
          <MenuItem onClick={handleMarkAsRead}>
            <ListItemIcon>
              <MarkEmailRead fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Read</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleArchiveConversation}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleDeleteConversation}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PpMessagingCenterPage;
